import styled from "@emotion/styled";
import React, { useRef, useEffect, useState, memo } from "react";

import { Link } from "@brexhq/next/src/components/shared/Link/Link";
import { useOptimizedBreakpoint } from "@brexhq/next/src/hooks/useOptimizedBreakpoint";
import ArrowRightMono from "@brexhq/shared/assets/icons/single-color/ArrowRightMono";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { ThemeTextComponents } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextRow";

interface ManifestoInlineCtaProps {
  value: {
    openedByDefault: boolean;
    isFullWidth: boolean;
    isCentered: boolean;
    callToAction: {
      label: string;
      to: string;
      toExternal: string;
      noFollow: boolean;
    };
  };
  isInView?: boolean;
}

const DEFAULT_HOVER_WIDTH = 220;
const DESKTOP_SIZE = 48;
const MOBILE_SIZE = 32;
const TABLET_SIZE = 36;
const PADDING = 24;
const IN_VIEW_DELAY = 1000;
const PROXIMITY_DISTANCE = 100;

const HoverWrapper = styled.div<{
  isMobile: boolean;
  isFullWidth: boolean;
  hoverWidth: number;
  isOpenedByDefault: boolean;
}>(({ isFullWidth, hoverWidth, isOpenedByDefault, isMobile }) => ({
  position: "relative",
  display: isFullWidth ? "block" : "inline-block",
  padding:
    isMobile || isFullWidth || isOpenedByDefault
      ? 0
      : `${PROXIMITY_DISTANCE}px`,
  margin:
    isMobile || isFullWidth || isOpenedByDefault
      ? 0
      : `-${PROXIMITY_DISTANCE}px`,
  backgroundColor: "transparent",
  zIndex: 1,
  "&:hover > div": {
    width: isFullWidth ? "100%" : `${hoverWidth}px`,
    transition: "width .6s cubic-bezier(.7,0,.3,1)",
    "& > a, & > div": {
      opacity: "1 !important",
      transition:
        "transform .5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s, opacity .65s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important",
      transform: "translateY(0px)",
    },
  },
}));

const Wrapper = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
  inView?: boolean;
  isFullWidth: boolean;
  openedByDefault: boolean;
  isCentered: boolean;
}>(
  ({
    theme,
    inView,
    isMobile,
    isTablet,
    isFullWidth,
    openedByDefault,
    isCentered,
  }) => ({
    position: "relative",
    display: isFullWidth ? "block" : "inline-block",
    width:
      inView || openedByDefault
        ? isFullWidth
          ? "100%"
          : isMobile || isTablet
          ? "min-content"
          : DESKTOP_SIZE
        : 0,
    height: isMobile ? MOBILE_SIZE : isTablet ? TABLET_SIZE : DESKTOP_SIZE,
    backgroundColor: "black",
    transition: "width .6s cubic-bezier(0.25, 0.46, 0.45, 0.94) .15s",
    overflow: "hidden",
    ...(isFullWidth && {
      margin: isCentered ? "0 auto" : "0",
      maxWidth: "fit-content",
    }),
    marginTop: isFullWidth ? "20px" : 0,
    "& > a, & > div": {
      opacity: isMobile || isTablet || openedByDefault ? "1" : "0",
      transition:
        "transform .5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity .5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      transform:
        isMobile || isTablet || openedByDefault
          ? "translateY(0px)"
          : "translateY(40px)",
      width: "100%",
    },
    "& span": {
      letterSpacing: "0px !important",
      transition: "color .15s ease-in-out",
    },
    "&:hover span": {
      color: theme.colors.content.disabled,
    },
  }),
);

const CtaButton = styled(Link, {
  shouldForwardProp: (prop) => prop !== "_type" && prop !== "toExternal",
})<{ isFullWidth: boolean }>(({ isFullWidth }) => ({
  textDecoration: "none !important",
  display: "flex",
  alignItems: "center",
  justifyContent: isFullWidth ? "center" : "flex-start",
  height: "100%",
  width: "100%",
  "&:hover": {
    opacity: "1 !important",
  },
  "& > span": {
    whiteSpace: "nowrap",
    margin: "0 24px",
  },
}));

const CtaDiv = styled.div<{ isFullWidth: boolean }>(({ isFullWidth }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isFullWidth ? "center" : "flex-start",
  height: "100%",
  width: "100%",
  cursor: "default",
  "& > span": {
    whiteSpace: "nowrap",
    margin: "0 24px",
  },
  "&:hover": {
    opacity: "1 !important",
  },
}));

function ManifestoInlineCtaComponent({
  value,
  isInView,
}: ManifestoInlineCtaProps) {
  const {
    callToAction,
    isFullWidth,
    openedByDefault = false,
    isCentered = false,
  } = value;
  const textRef = useRef<HTMLSpanElement>(null);
  const [hoverWidth, setHoverWidth] = useState(DEFAULT_HOVER_WIDTH);

  const { isMobile, isTablet } = useOptimizedBreakpoint();

  const [isView, setIsView] = useState(false);

  useEffect(() => {
    const calculateWidth = () => {
      if (textRef.current) {
        const textWidth = textRef.current.getBoundingClientRect().width;
        const calculatedWidth = Math.max(textWidth + PADDING * 2, DESKTOP_SIZE);
        setHoverWidth(calculatedWidth);
      }
    };

    calculateWidth();

    window.addEventListener("resize", calculateWidth);

    return () => {
      window.removeEventListener("resize", calculateWidth);
    };
  }, [callToAction.label]);

  useEffect(() => {
    if (isInView === isView) {
      return;
    }

    if (isInView) {
      const timer = setTimeout(() => setIsView(isInView), IN_VIEW_DELAY);
      return () => clearTimeout(timer);
    }

    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  const CtaComponent =
    callToAction.to || callToAction.toExternal ? CtaButton : CtaDiv;

  const renderLabelWithIcon = (label: string) => {
    if (!label) {
      return null;
    }

    const parts = label.split("->");
    if (parts.length === 1) {
      return label;
    }

    return parts.map((part, index) => {
      const key = `${part}-${Math.random()}`;
      return (
        <React.Fragment key={key}>
          {part}
          {index < parts.length - 1 && (
            <ArrowRightMono width="10" height="10" />
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <HoverWrapper
      isMobile={isMobile || isTablet}
      hoverWidth={hoverWidth}
      isFullWidth={isFullWidth}
      isOpenedByDefault={openedByDefault || isMobile || isTablet}
    >
      <Wrapper
        inView={isView}
        isMobile={isMobile}
        isTablet={isTablet}
        isFullWidth={isFullWidth}
        openedByDefault={openedByDefault || isMobile || isTablet}
        isCentered={isCentered}
      >
        <CtaComponent
          {...callToAction}
          component="manifesto-inline-cta"
          modifier="unstyled"
          isFullWidth={isFullWidth}
        >
          <Text
            ref={textRef}
            as="span"
            components={ThemeTextComponents["Space Mono S"].components}
            fontWeightOverride="REGULAR"
            color="white"
          >
            {renderLabelWithIcon(callToAction.label)}
          </Text>
        </CtaComponent>
      </Wrapper>
    </HoverWrapper>
  );
}

export const ManifestoInlineCta = memo(ManifestoInlineCtaComponent);
